import React, {useMemo, useRef, useState} from 'react';
import {Box, Chip, InputAdornment, TextField, Typography} from '@mui/material';
import {Search} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {evaluate} from 'mathjs';

export function verifyFormula(formula, variables) {
    if (!formula?.trim()) return {valid: false, error: 'Enter a formula.'};
    // Replace known metric tokens before checking the remaining arithmetic expression.
    const expression = Object.keys(variables).reduce((result, token) => result.split(token).join('(1)'), formula);
    if (!/^[0-9+\-*/().\s]+$/.test(expression)) {
        return {valid: false, error: 'Use available metrics, numbers and arithmetic operators only.'};
    }
    try {
        const result = evaluate(expression);
        return typeof result === 'number' ? {valid: true} : {valid: false, error: 'Enter a valid arithmetic formula.'};
    } catch {
        return {valid: false, error: 'Check the operators and parentheses in your formula.'};
    }
}

export default function FormulaInput({columns, label, name, register, errors, errorMessage, watch, setValue, disabled = false}) {
    const inputRef = useRef(null);
    const cursorPosition = useRef(null);
    const [metricSearch, setMetricSearch] = useState('');
    const variables = useMemo(() => Object.fromEntries((columns || [])
        .filter(column => column.type === 'metric' || column.type === 'customEvent')
        .map(column => [`{${column.title}}`, 1])), [columns]);
    const tokens = Object.keys(variables).filter(token => token.toLowerCase().includes(metricSearch.trim().toLowerCase()));
    const registration = register(name, {required: 'Enter a formula.', validate: value => {
        const verification = verifyFormula(value, variables);
        return verification.valid || verification.error;
    }});
    const {ref: registerRef, ...field} = registration;
    const insertToken = token => {
        const currentValue = watch(name) || '';
        const start = cursorPosition.current?.start ?? currentValue.length;
        const end = cursorPosition.current?.end ?? start;
        const next = currentValue.slice(0, start) + token + currentValue.slice(end);
        const position = start + token.length;
        setValue(name, next, {shouldValidate: true, shouldDirty: true});
        cursorPosition.current = {start: position, end: position};
        requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(position, position);
        });
    };
    const rememberSelection = event => {
        cursorPosition.current = {start: event.target.selectionStart, end: event.target.selectionEnd};
    };
    const tokenStyles = {
        height: 28, fontSize: 12, borderRadius: '6px', maxWidth: '100%',
        color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.09),
        '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.16)},
        '& .MuiChip-label': {px: 1}
    };

    return <Box sx={{display: 'grid', gap: 1.5, minWidth: 0}}>
        <TextField {...field} inputRef={node => {registerRef(node); inputRef.current = node;}}
            value={watch(name) || ''} label={label} required size="small" fullWidth disabled={disabled}
            placeholder="{Cost} / {Conversions (Approved)}" error={errors} helperText={errorMessage}
            slotProps={{inputLabel: {shrink: true}, htmlInput: {onSelect: rememberSelection}}}
            sx={{'& .MuiOutlinedInput-input': {fontFamily: 'monospace'}}}
            onChange={event => {
                rememberSelection(event);
                setValue(name, event.target.value, {shouldValidate: true, shouldDirty: true});
            }}/>
        <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
            {['(', ')', '+', '-', '*', '/'].map(operator => <Chip key={operator} label={operator} disabled={disabled}
                aria-label={`Insert ${operator}`} onMouseDown={event => event.preventDefault()} onClick={() => insertToken(operator)}
                sx={{...tokenStyles, minWidth: 30, fontFamily: 'monospace', fontSize: 15, color: 'text.secondary',
                    bgcolor: theme => alpha(theme.palette.text.primary, 0.06), '&:hover': {bgcolor: theme => alpha(theme.palette.text.primary, 0.1)}}}/>)}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap'}}>
            <Typography sx={{fontSize: 12, fontWeight: 600, color: 'text.secondary'}}>Available metrics</Typography>
            {Object.keys(variables).length > 8 && <TextField size="small" placeholder="Find a metric" disabled={disabled}
                value={metricSearch} onChange={event => setMetricSearch(event.target.value)}
                sx={{width: {xs: '100%', sm: 210}, '& .MuiOutlinedInput-root': {height: 32}}}
                slotProps={{htmlInput: {'aria-label': 'Find a metric'}, input: {startAdornment:
                    <InputAdornment position="start"><Search sx={{fontSize: 17, color: 'text.secondary'}}/></InputAdornment>}}}/>}
        </Box>
        <Box sx={{display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 0.75, maxHeight: 148, overflowY: 'auto', minWidth: 0}}>
            {tokens.map(token => <Chip key={token} label={token} disabled={disabled} sx={tokenStyles}
                onMouseDown={event => event.preventDefault()} onClick={() => insertToken(token)}/>)}
            {!tokens.length && <Typography sx={{fontSize: 12, color: 'text.secondary'}}>
                {metricSearch ? 'No matching metrics.' : 'No metrics available.'}
            </Typography>}
        </Box>
    </Box>;
}
